import { Button, Group, Text, Textarea } from "@mantine/core"
import React from "react"
import { useTranslation } from "react-i18next"
import { mutate } from "swr"
import { OrderApi } from "../../apis"
import { IOrderPlus } from "../../models/Order"
import { currencyFormat } from "../../utils"
import HttpHandler from "../../utils/http-handler"
import { message } from "../../utils/message"
import { OrderActionCard } from "../order/order-action-card"
import { useSalesActions } from "../order/order-actions"
import { ReasonTooltip } from "../ui"

/**
 * Бөөнөөр татгалзах: the row's decline (BE order/respond.ts, approve=false) applied to each selected
 * PO with one reason. There is no bulk decline endpoint, so orders go one by one and a partial
 * failure is reported by code, like the bulk approve.
 */
export function PurchaseBulkRejectForm({ orders, onCancel, reload, setChecked, onBusyChange }: {
  orders: IOrderPlus[]
  onCancel: () => void
  reload: () => void
  setChecked: (rows: IOrderPlus[]) => void
  /** Lets the page keep the modal open while the loop runs (Esc / outside click would orphan it). */
  onBusyChange?: (busy: boolean) => void
}) {
  const { t } = useTranslation(["order", "supplierSales", "common"])
  const [loading, setLoading] = React.useState(false)
  const [reason, setReason] = React.useState("")
  const [reasonError, setReasonError] = React.useState(false)
  const { permissionState } = useSalesActions()
  const perm = permissionState("decline")
  const total = orders.reduce((sum, o) => sum + Number(o.orderAmount || 0), 0)

  const cancel = () => {
    if (!loading) onCancel()
  }

  const onSubmit = async () => {
    if (loading) return
    if (!reason.trim()) {
      setReasonError(true)
      return
    }
    setLoading(true)
    onBusyChange?.(true)
    const failed: string[] = []
    try {
      for (const o of orders) {
        try {
          await OrderApi.respond(o.id!, { approve: false, reason: reason.trim() })
        } catch (err) {
          failed.push(o.purchaseCode || o.salesCode || o.id || "-")
          if (orders.length === 1) message.error((err as HttpHandler)?.message!)
        }
      }
      mutate("swr.order.count.customer")
      mutate("swr.order.count.sales")
      setChecked([])
      if (failed.length === 0) message.success(t("order:purchaseSales.orderRejectedSuccess"))
      else if (orders.length > 1) {
        message.error(t("supplierSales:purchase.bulkDeclinePartial", { done: orders.length - failed.length, failed: failed.length, codes: failed.join(", ") }))
      }
      reload()
    } finally {
      setLoading(false)
      onBusyChange?.(false)
    }
    onCancel()
  }

  return (
    <OrderActionCard
      title={t("supplierSales:purchase.bulkDecline")}
      onClose={cancel}
      loading={loading}
      footer={
        <>
          <Button variant="default" onClick={cancel}>{t("common:actions.dismiss")}</Button>
          <ReasonTooltip reason={perm.reason}>
            <Button color="gray" variant="light" onClick={onSubmit}>{t("order:purchaseSales.reject")}</Button>
          </ReasonTooltip>
        </>
      }
    >
      <Text fz={14}>{t("supplierSales:purchase.bulkDeclineBody", { count: orders.length })}</Text>
      <Group justify="space-between" wrap="nowrap">
        <Text fz={14} c="gray.8">{t("supplierSales:purchase.bulkDeclineCount")}</Text>
        <Text fz={14} fw={700}>{orders.length}</Text>
      </Group>
      <Group justify="space-between" wrap="nowrap">
        <Text fz={14} c="gray.8">{t("order:purchaseSales.orderAmount")}</Text>
        <Text fz={14} fw={700} ff="monospace">{currencyFormat(total)}</Text>
      </Group>
      <Textarea
        label={t("supplierSales:purchase.declineReason")}
        placeholder="-"
        required
        minRows={3}
        autosize
        maxLength={500}
        value={reason}
        onChange={(e) => {
          setReason(e.currentTarget.value)
          if (reasonError) setReasonError(false)
        }}
        error={reasonError ? t("supplierSales:purchase.declineReasonRequired") : undefined}
      />
    </OrderActionCard>
  )
}
